import React from 'react';
import { motion } from 'framer-motion';
import { Diagnosis } from '../utils/diagnosis';
import { routineWhatsappUrl } from '../utils/whatsapp';
import { trackEventOnce } from '../utils/metaPixel';
type ClosingProps = {
  diagnosis: Diagnosis;
};
const benefits = ['Qué usar por la mañana y por la noche.', 'En qué orden aplicar cada producto.', 'Recomendaciones adaptadas a tu tipo de piel.', 'Acompañamiento personalizado para resolver tus dudas.'];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 22
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    amount: 0.35
  }
};
export function Closing({
  diagnosis
}: ClosingProps) {
  const link = routineWhatsappUrl(diagnosis.skinTypeLabel, diagnosis.score);
  const areas = diagnosis.improvements.slice(0, 3).map(item => item.label.toLowerCase()).join(', ');
  return <div className="mt-24" data-id="element-18" data-mp="uh13vhcxat2d">
      {/* Puente: del resultado al siguiente paso */}
      <motion.h2 {...fadeUp} transition={{
      duration: 1,
      ease: [0.22, 1, 0.36, 1]
    }} className="text-center font-serif text-[2rem] font-light leading-[1.15] text-warmgray-800 sm:text-[2.75rem]" data-id="element-19" data-mp="yk77sickc85o">
        Ahora sabemos qué necesita tu piel.
      </motion.h2>

      <motion.p {...fadeUp} transition={{
      duration: 1,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="mx-auto mt-7 max-w-lg text-center text-base font-light leading-relaxed text-warmgray-600" data-id="element-20" data-mp="4l31mw16jj24">
        Con un Skin Fresh Index™ de {diagnosis.score}/100 y una piel{' '}
        {diagnosis.skinTypeLabel.toLowerCase()}, el mayor margen de mejora está
        en {areas}. El próximo paso es traducir eso en una rutina concreta.
      </motion.p>

      {/* Siguiente paso */}
      <motion.section {...fadeUp} transition={{
      duration: 1,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="mt-16 rounded-4xl border border-beige-300 bg-white px-7 py-11 sm:px-12 sm:py-14" data-id="element-21" data-mp="3d0mpffj3099s">
        <h3 className="font-serif text-[1.65rem] font-light leading-tight text-warmgray-800 sm:text-3xl" data-id="element-22" data-mp="se34qw5v6fjo">
          ¿Querés que te arme tu rutina personalizada?
        </h3>

        <p className="mt-6 text-sm font-light leading-relaxed text-warmgray-600 sm:text-base" data-id="element-23" data-mp="14coxi2vto9jp">
          Ya conocemos tu tipo de piel y tu resultado. Puedo orientarte sobre qué
          usar, en qué orden y qué productos realmente necesitás, sin sumar
          productos innecesarios.
        </p>

        <ul className="mt-9 flex flex-col gap-4" data-id="element-24" data-mp="gq8pr1bjx8al">
          {benefits.map(item => <li key={item} className="flex items-start gap-4" data-id="element-25" data-mp="lojzlc1ujist">
              <span aria-hidden="true" className="mt-0.5 text-sage-600" data-id="element-26" data-mp="qmbodx772oss">
                ✦
              </span>
              <span className="text-sm font-light leading-relaxed text-warmgray-700 sm:text-base" data-id="element-27" data-mp="vdtpcl97wpdr">
                {item}
              </span>
            </li>)}
        </ul>
      </motion.section>

      {/* Regalo */}
      <motion.aside {...fadeUp} transition={{
      duration: 1,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="mx-auto mt-10 max-w-md rounded-3xl border border-dashed border-sage-300 bg-sage-50/70 px-7 py-8 text-center sm:px-10" data-id="element-28" data-mp="2vps136jysym">
        <p className="text-[0.62rem] uppercase tracking-[0.3em] text-sage-700" data-id="element-29" data-mp="276ciwf76bgi9">
          🎁 Un regalo para vos
        </p>
        <p className="mt-5 font-serif text-2xl font-light leading-snug text-warmgray-800 sm:text-[1.75rem]" data-id="element-30" data-mp="226py9kp0mxzf">
          Tenés <span className="italic" data-id="element-31" data-mp="1xa96mgily1hl">$44.000</span> de descuento
        </p>
        <p className="mt-3 text-sm font-light text-warmgray-600" data-id="element-32" data-mp="1sgww1aarjv7u">
          Un voucher exclusivo para tu primera compra.
        </p>
        <p className="mt-5 text-[0.65rem] font-light leading-relaxed text-warmgray-400" data-id="element-33" data-mp="1ogmd6x1vhabk">
          Válido para nuevos clientes. Aplican condiciones del voucher.
        </p>
      </motion.aside>

      {/* CTA */}
      <motion.div {...fadeUp} transition={{
      duration: 1,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="mt-16 flex flex-col items-center" data-id="element-34" data-mp="1ekaxry6zk4cw">
        <motion.a href={link} target="_blank" rel="noopener noreferrer" onClick={() => trackEventOnce('Contact', {
        content_name: 'WhatsApp — Rutina personalizada',
        skin_type: diagnosis.skinTypeLabel,
        value: diagnosis.score
      })} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.99
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="w-full rounded-full bg-sage-700 px-12 py-7 text-center text-sm uppercase tracking-[0.18em] text-beige-50 transition-colors duration-700 ease-calm hover:bg-sage-800 sm:w-auto sm:px-20" data-id="element-35" data-mp="39lv6rdc2h4bc">
          Quiero mi rutina personalizada
        </motion.a>

        <p className="mt-7 max-w-sm text-center text-xs font-light leading-relaxed text-warmgray-500" data-id="element-36" data-mp="f4szo7lnkqc3">
          Te orientamos personalmente por WhatsApp · Sin compromiso
        </p>

        <p className="mt-3 max-w-sm text-center text-[0.7rem] font-light leading-relaxed text-warmgray-400" data-id="element-37" data-mp="24m6ptq6ouwoe">
          Es rápido, personalizado y no implica ningún compromiso.
        </p>
      </motion.div>
    </div>;
}